import { Box, Typography } from '@mui/material';
import { useOnboardingLens } from './onboardingLens';

interface OnboardingStepHeaderProps {
  title: string;
  subtitle: string;
  /** Gap between title and subtitle — Lens spacing token value. */
  titleSubtitleGap?: string;
  /** Space below the subtitle before step content (Lens spacing token). */
  subtitleBottomSpacing?: string;
}

/** Figma Page header — node 16692:5506 (H2 Display + Text/Md). */
export function OnboardingStepHeader({
  title,
  subtitle,
  titleSubtitleGap,
  subtitleBottomSpacing,
}: OnboardingStepHeaderProps) {
  const { layout, typography, typeDefault } = useOnboardingLens();
  const gap = titleSubtitleGap ?? layout.formField.labelGap;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: gap,
        alignSelf: 'stretch',
        width: '100%',
        flexShrink: 0,
        ...(subtitleBottomSpacing !== undefined ? { pb: subtitleBottomSpacing } : {}),
      }}
    >
      <Typography component="h2" sx={{ ...typography.h2Display, color: typeDefault }}>
        {title}
      </Typography>
      <Typography sx={{ ...typography.textMd, color: typeDefault }}>{subtitle}</Typography>
    </Box>
  );
}

interface OnboardingFieldLabelProps {
  label: string;
  required?: boolean;
  htmlFor?: string;
  disabled?: boolean;
}

/** Figma form field label — node I16692:5508;7919:11304 */
export function OnboardingFieldLabel({
  label,
  required,
  htmlFor,
  disabled = false,
}: OnboardingFieldLabelProps) {
  const { layout, typography, typeDefault, accentRedRequired } = useOnboardingLens();

  return (
    <Box
      component="label"
      htmlFor={htmlFor}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: layout.formField.labelGap,
        minHeight: layout.regions.labelHeight,
      }}
    >
      <Typography
        component="span"
        sx={{
          ...typography.labelEmphasis,
          color: disabled ? 'text.disabled' : typeDefault,
        }}
      >
        {label}
      </Typography>
      {required && (
        <Typography
          component="span"
          sx={{
            ...typography.labelRequired,
            color: disabled ? 'text.disabled' : accentRedRequired,
          }}
        >
          *
        </Typography>
      )}
    </Box>
  );
}
